import { Card, CARD_SECTION_TITLE_CLASS } from '@safetyhub/ui';
import { useUsageStore, getUsagePercentage, getUsageColor, getTimeUntilReset } from '../../stores/usage';

export default function Billing() {
  const { usage, isLoading } = useUsageStore();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-ink-200 rounded w-48 mb-4"></div>
          <div className="h-4 bg-ink-200 rounded w-64"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">구독 관리</h1>
        <p className="mt-1 text-[15px] font-medium text-ink-500">
          사용량을 확인하고 요금제를 관리합니다.
        </p>
      </div>

      {/* 테스트 단계 안내 */}
      <div className="bg-brand-50 border border-brand-200 rounded-card p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <InfoIcon className="h-5 w-5 text-brand-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-brand-800">테스트 단계 운영 중</h3>
            <p className="mt-1 text-sm text-brand-700">
              현재 테스트 단계로 무료로 서비스를 이용하실 수 있습니다.
              일일 사용량 제한이 적용되며, 매일 자정(KST)에 초기화됩니다.
            </p>
          </div>
        </div>
      </div>

      {/* 오늘의 사용량 */}
      {usage && (
        <Card padding="xl" radius="lg" elevation="md">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={CARD_SECTION_TITLE_CLASS}>오늘의 사용량</h2>
              <p className="text-sm text-ink-500">{usage.date} (KST)</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-ink-500">요금제</p>
              <p className="text-lg font-semibold text-brand-600">무료 (테스트)</p>
            </div>
          </div>

          {/* 사용량 */}
          <div className="border-t border-line pt-4">
            <h3 className="text-sm font-medium text-ink-700 mb-3">일일 사용량</h3>

            {/* 점검 횟수 */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-ink-600">점검 생성</span>
                <span className="font-medium">
                  {usage.inspectionCount} / {usage.inspectionLimit}회
                </span>
              </div>
              <div className="w-full bg-ink-200 rounded-full h-2">
                <div
                  className={`${getUsageColor(getUsagePercentage(usage.inspectionCount, usage.inspectionLimit))} h-2 rounded-full transition-all`}
                  style={{ width: `${getUsagePercentage(usage.inspectionCount, usage.inspectionLimit)}%` }}
                />
              </div>
            </div>

            {/* 사진 분석 */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-ink-600">사진 업로드</span>
                <span className="font-medium">
                  {usage.photoCount} / {usage.photoLimit}장
                </span>
              </div>
              <div className="w-full bg-ink-200 rounded-full h-2">
                <div
                  className={`${getUsageColor(getUsagePercentage(usage.photoCount, usage.photoLimit))} h-2 rounded-full transition-all`}
                  style={{ width: `${getUsagePercentage(usage.photoCount, usage.photoLimit)}%` }}
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-3 text-sm text-ink-500">
              <ClockIcon className="h-4 w-4" />
              <span>다음 초기화까지 {getTimeUntilReset(usage.nextResetAt)}</span>
            </div>
          </div>
        </Card>
      )}

      {/* 요금제 안내와 문의는 둘 다 내용이 짧아 전폭으로 늘이면 여백만 커진다 — 넓은 화면에서만 나란히.
          (사용량 카드는 조건부라 짝을 묶으면 한쪽이 비는 경우가 생겨 전폭으로 둔다.) */}
      <div className="space-y-6 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0 lg:items-start">
      {/* 요금제 안내 */}
      <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
        <div className="px-6 py-4 border-b border-line bg-surface-soft">
          <h2 className={CARD_SECTION_TITLE_CLASS}>요금제 안내</h2>
          <p className="text-sm text-ink-500">정식 출시 후 다양한 요금제가 제공됩니다.</p>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <div className="mx-auto h-12 w-12 rounded-full bg-surface-soft flex items-center justify-center mb-4">
              <SparklesIcon className="h-6 w-6 text-ink-400" />
            </div>
            <h3 className="text-[15px] font-extrabold tracking-tight text-ink-900 mb-2">정식 출시 준비 중</h3>
            <p className="text-sm text-ink-500 max-w-md mx-auto">
              정식 서비스 출시 시 개인용/기업용 요금제가 제공될 예정입니다.
              테스트 기간 동안 무료로 서비스를 이용해 주세요.
            </p>
          </div>
        </div>
      </Card>

      {/* 문의 안내 */}
      <Card surface="soft" elevation="none">
        <h3 className="text-sm font-medium text-ink-900 mb-2">기업 고객 문의</h3>
        <p className="text-sm text-ink-600">
          대규모 사용이 필요하시거나 맞춤형 서비스가 필요하신 경우 문의해 주세요.
        </p>
        <a
          href="mailto:support@safetyhub.kr"
          className="mt-3 inline-block text-sm text-brand-600 hover:text-brand-700"
        >
          support@safetyhub.kr
        </a>
      </Card>
      </div>
    </div>
  );
}

// Icons
function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}
